import { z } from 'zod'
import type { ResourceType } from '@medplum/fhirtypes'
import { oc } from '@orpc/contract'

/**
 * PD-1322 — Chart Audit contracts (decision-independent slice).
 *
 * These schemas are the seam that keeps the audit feature storage-agnostic:
 * the web UI and the middleware handlers both type against them, so Roman's
 * pending storage decision (D1) can swap the `AuditStore` implementation
 * without touching either side.
 */

/**
 * Where an access event originated. Values mirror the mockup source badges.
 *
 * The three AUDIT_ACCESS_*_VALUES tuples below are also the source of the
 * `audit_access_event` text+CHECK columns (src/db/schema/audit.ts, re-exported
 * for server-side code via src/audit/audit-shared.ts), so the database
 * vocabulary can never drift from the API's.
 */
/**
 * The ticket names the sources individually — "BeHuman UI, API, AI service, TEFCA, Health
 * Gorilla, eRx or other integration" — so a single collapsed `integration` value cannot
 * answer "did this record leave through TEFCA or through Health Gorilla", which is the
 * question an external-exchange audit actually asks. S21 splits it.
 *
 * `integration` is RETAINED rather than replaced. The CHECK constraint on
 * `audit_access_event.source` is validated against existing rows when migration 0011
 * rewrites it, and the table is append-only (UPDATE/DELETE revoked AND blocked by a
 * trigger), so a row written under migration 0010 with `source = 'integration'` could
 * neither pass the new constraint nor be rewritten or removed — the migration would be
 * unfixable. Keeping the value costs one tuple entry. Precedent for retaining a superseded
 * enum value for exactly this reason: `care-coordination/dto/billing-status.contract.ts`
 * keeps the legacy `pending`/`billed` codes "so existing encounters migrate with no data
 * rewrite". No code writes `integration` any more — new integration writes pick their
 * specific value, or `other-integration`.
 */
export const AUDIT_ACCESS_SOURCE_VALUES = [
  'behuman-web',
  'ai-service',
  'api',
  'tefca',
  'health-gorilla',
  'erx',
  'other-integration',
  /**
   * S28 — captured by ingesting Medplum's own `AuditEvent` stream out of CloudWatch, rather
   * than reported by a client or observed through a Subscription.
   *
   * A CAPTURE MECHANISM, not a channel, and that is deliberate. The other values answer
   * "where did this access come from"; this one answers "how do we know about it", because
   * for an ingested event that is the only question we can answer honestly. Medplum's
   * AuditEvent names the FHIR interaction and its author (`agent.who`), so a bot read is
   * distinguishable from a practitioner read via `actorReference` — but a practitioner token
   * used from the web app and the same token used against the API produce identical events,
   * so labelling one of them `behuman-web` or `api` would be a guess presented as a record.
   * The row says what the log said.
   */
  'medplum-audit',
  /** Superseded by the four values above; retained only so migration 0011 can apply. */
  'integration',
] as const
export const auditAccessSourceSchema = z.enum(AUDIT_ACCESS_SOURCE_VALUES)
export type AuditAccessSource = z.infer<typeof auditAccessSourceSchema>

export const AUDIT_ACCESS_RESULT_VALUES = ['success', 'failure', 'denied'] as const
export const auditAccessResultSchema = z.enum(AUDIT_ACCESS_RESULT_VALUES)
export type AuditAccessResult = z.infer<typeof auditAccessResultSchema>

export const AUDIT_ACCESS_TYPE_VALUES = ['standard', 'emergency'] as const
export const auditAccessTypeSchema = z.enum(AUDIT_ACCESS_TYPE_VALUES)
export type AuditAccessType = z.infer<typeof auditAccessTypeSchema>

/**
 * A boolean that survives GET query-string transport (QA round 1, W3 — the
 * downloads/exports toggle "did nothing").
 *
 * The list endpoints are `GET` routes, so `OpenAPILink` serializes their input into the
 * query string, where every value is stringified (`params.append(path, value.toString())`
 * in @orpc/openapi-client's StandardOpenAPISerializer) — a `downloadsOnly: true` arrives
 * server-side as the STRING `'true'`. No coercion plugin is configured on the Nest side,
 * so a plain `z.boolean()` rejected it and the whole request failed input validation:
 * the toggle didn't filter, it emptied the table. This schema accepts both the typed
 * value (POST bodies, in-process tests) and its query-string spelling, and always
 * OUTPUTS a boolean.
 */
const queryBooleanSchema = z.union([
  z.boolean(),
  z.enum(['true', 'false']).transform((value) => value === 'true'),
])

/** Most sections one view report may carry. The chart has far fewer than this. */
export const AUDIT_SECTIONS_MAX_COUNT = 64
/** Rows one access-events response may carry before it reports itself truncated. */
export const AUDIT_ACCESS_EVENT_PAGE_SIZE = 10_000

/**
 * The chart's section vocabulary: every `dataKey` the chart's own section registry
 * declares (`web/src/pages/Patient/components/PatientChart.utils.tsx`), and the CLOSED set
 * of values {@link auditReportView} will accept.
 *
 * QA round 3 (independent re-test) — the view endpoint used to bound its `sections` for
 * SIZE and leave them open for CONTENT (`z.string().min(1).max(128)`). Proven on dev: an
 * ordinary bearer token POSTing `{"sections":["__bogus__"]}` got a 200, and `__bogus__`
 * then appeared permanently in that patient's Section filter dropdown, because
 * `/audit/filter-options` derives its options from the sections rows were actually written
 * with. `audit_access_event` is append-only (UPDATE/DELETE revoked AND blocked by a
 * trigger), so the injected row can never be removed — any authenticated caller could
 * write text into any patient's compliance filter list, permanently.
 *
 * A SUPERSET of the {@link SECTION_RESOURCE_TYPES} keys, and that distinction is the whole
 * point. That map answers "which FHIR types back this section's CHANGE events" and only
 * 13 sections have any; ACCESS is reported for every section the chart routes to,
 * including the ones with no resources of their own (`timeline`, `clinical-summary`,
 * `risks-gaps`, `social-history`, `order-tests`,
 * `history-physical`, `audit`, `demographics`, `insurances`). Validating against the
 * change map would have silently stopped recording views of half the chart.
 *
 * A STALE VOCABULARY FAILS THE BUILD, NOT THE REQUEST. A new chart section whose key is
 * missing here would get a 400 from the view endpoint — and because the web treats
 * reporting as fire-and-forget, nobody would see it: the section's views would simply stop
 * being recorded, which is a worse compliance failure than the injection this closes. So
 * the drift is caught before it ships: `web/src/pages/Patient/auditChangeCoverage.test.ts`
 * holds the chart registry against this list, in the same partition gate that already
 * holds it against `SECTION_RESOURCE_TYPES`, and fails the suite if a registry dataKey is
 * missing from either.
 *
 * Lives in this file, not beside the server-only section helpers, for the same reason
 * `SECTION_RESOURCE_TYPES` does: the gate runs in `web`, and a Vite plugin
 * (`fail-on-server-imports`) rejects any middleware module that is not a `*.contract.ts`
 * being pulled into a frontend bundle. Imported by the gate, never mirrored — a mirror is
 * a copy, and a copy is what drifts.
 */
export const AUDIT_SECTION_KEYS = [
  'clinical-summary',
  'risks-gaps',
  'demographics',
  'care-coordination-notes',
  'care-coordination-tasks',
  'timeline',
  'progress-notes',
  'order-tests',
  'orders',
  'labs-imaging',
  'documents',
  'consents',
  'vitals',
  'history-physical',
  'insurances',
  'diagnosis',
  'medications',
  'allergies',
  'immunizations',
  'procedures',
  'family-history',
  'social-history',
  'encounters',
  'audit',
] as const
export const auditSectionKeySchema = z.enum(AUDIT_SECTION_KEYS)
export type AuditSectionKey = z.infer<typeof auditSectionKeySchema>

/**
 * Whether a string is one of the chart's section keys.
 *
 * The web knows a value is a registry dataKey by having resolved it against the chart
 * registry; this turns that knowledge into something the type system carries, so the
 * closed vocabulary is enforced at the call site rather than asserted through a cast.
 */
export function isAuditSectionKey(value: string | undefined): value is AuditSectionKey {
  return value !== undefined && (AUDIT_SECTION_KEYS as readonly string[]).includes(value)
}

/**
 * Chart section (dataKey) -> the FHIR resource types whose version history
 * backs that section's change events. A section-filtered request fans out
 * over ONLY that section's types (lazy per-section loading, S9); an
 * unfiltered request walks the union.
 *
 * Lives in its own module because two sides consume it and neither may import
 * the other: `AuditService` uses it to scope the derivation SYNC, and the
 * `ChangeEventStore` implementations use it to turn the contract's `section`
 * filter into a `resource_type IN (...)` predicate. The two MUST agree — a
 * section that syncs one type set but queries another would return rows the
 * walk never refreshed, or hide rows it just wrote.
 */
export const SECTION_RESOURCE_TYPES: Record<string, ResourceType[]> = {
  demographics: ['Patient'],
  insurances: ['Coverage'],
  vitals: ['Observation'],
  // `RequestGroup` groups the ServiceRequests placed together as one order; the chart's
  // orders section renders both. Found by the coverage gate, not by hand.
  // `DocumentReference` because an order's uploaded paperwork carries document-type code
  // `Order` (QA round 3 — those documents' events labelled themselves 'documents', so an
  // order opened from the Orders tab audited as if it came from the Documents tab, and
  // the orders filter could not reach them). Subtype-narrowed like the other
  // DocumentReference-backed sections — see documentSubtypeFilter.
  orders: ['ServiceRequest', 'RequestGroup', 'DocumentReference'],
  'labs-imaging': ['DiagnosticReport'],
  // QA round 3 — the History and Physical section is DocumentReference with document-type
  // code `HistoryPhysical` (the chart's own query is `type=HistoryPhysical`), NOT a
  // rendering of encounter resources as this section's old coverage-gate exemption
  // claimed. Untracked, its events fell into 'documents': visible under the wrong filter,
  // invisible under its own.
  'history-physical': ['DocumentReference'],
  // A signed consent is a DocumentReference alongside the `Consent` resource, so the
  // section needs both or a consent document's changes are unreachable under this filter.
  consents: ['Consent', 'DocumentReference'],
  // S19 — the ticket's billing domain ("Billing codes, modifiers, linked diagnoses and
  // claim status") lands under `encounters` because the chart has NO billing section:
  // PatientChart.utils.tsx registers 24 sections and none of them is billing (billing
  // lives at the top-level /billing route and inside the progress-note billing card).
  // Encounters is where a reader looks for what happened at a visit, and all three of
  // these resources are per-visit.
  //
  //  - Claim carries codes (`item[].productOrService`), modifiers (`item[].modifier`)
  //    and linked diagnoses (`diagnosis[]` joined by `item[].diagnosisSequence`) —
  //    written by visit/handlers/update-visit-billing.handler.ts:91-97 and
  //    billing/handlers/billing-save.handler.ts.
  //  - Encounter carries CLAIM STATUS, which is NOT `Claim.status` (hardcoded 'active'
  //    at visit.utils.ts:1009 and billing-save.handler.ts:116) but a `meta.tag` coding
  //    under https://behuman.health/fhir/CodeSystem/encounter-billing-status — the six
  //    values in care-coordination/dto/billing-status.contract.ts:14-32. Seeing those
  //    changes is why ChangeHistoryService diffs `meta.tag` (see META_TAG_FIELD).
  encounters: ['Appointment', 'Encounter', 'Claim'],
  'progress-notes': ['DocumentReference'],
  documents: ['DocumentReference'],
  medications: ['MedicationStatement'],
  diagnosis: ['Condition'],
  allergies: ['AllergyIntolerance'],
  /**
   * QA round 2 — four chart sections whose resource types this map never covered, so
   * their changes were invisible no matter how the derivation behaved. Found by diffing
   * this map against the chart's own section registry
   * (`web/.../PatientChart.utils.tsx`, which declares `resourceTypes` per section) after
   * QA reported missing care-coordination notes and procedures; the drift test in `web`
   * now fails the build when the two disagree, so the next section cannot be forgotten
   * the same way.
   *
   * `Communication` is scoped to the care-coordination category by
   * {@link SECTION_SEARCH_FILTERS} — practitioner message threads are patient-subject
   * Communications too, and they are neither chart data nor something whose body belongs
   * copied into the audit store.
   */
  'care-coordination-notes': ['Communication'],
  procedures: ['Procedure'],
  immunizations: ['Immunization'],
  'family-history': ['FamilyMemberHistory'],
}

/**
 * How a change was authored. Mirrors the mockup Manual / AI Generated / Integration badges.
 * A tuple (like the AUDIT_ACCESS_*_VALUES above) because since migration 0014 the values
 * also back the `audit_change_event.source` text+CHECK column.
 */
export const AUDIT_CHANGE_SOURCE_VALUES = ['manual', 'ai', 'integration'] as const
export const auditChangeSourceSchema = z.enum(AUDIT_CHANGE_SOURCE_VALUES)
export type AuditChangeSource = z.infer<typeof auditChangeSourceSchema>

/**
 * One access to the patient chart. Every record is authored and timestamped
 * server-side — the client never supplies `id`, `recordedAt`, or actor fields.
 */
export const auditAccessEventSchema = z.object({
  id: z.string(),
  /**
   * The patient whose chart was accessed — or `null` when the access is on record but its
   * patient could not be determined (S28).
   *
   * Nullable ONLY because of the ingest path. An `AuditEvent` for an `Observation` read names
   * the Observation, not the patient, and a resource that has since been deleted, or one with
   * no subject at all, cannot be resolved to a patient afterwards. The alternative was to drop
   * those events, and a compliance log that quietly discards what it cannot classify is worse
   * than one that admits the gap: the row is kept, `patient_id` is null, and
   * docs/db/DB_SCHEMA_AUDIT.md carries the query that counts them.
   *
   * Every event middleware authors itself still carries a patient. The read API is
   * patient-scoped (`WHERE patient_id = $1`), so a null-patient row is never returned by
   * `auditListAccessEvents` — it is reachable only by the reconciliation query.
   */
  patientId: z.string().nullable(),
  actorName: z.string(),
  actorRole: z.string().optional(),
  actorReference: z.string().optional(),
  /**
   * The actor's clinic/organization at the time of access (S22, the ticket's "User role
   * and clinic/organization"). Display name; see `actorOrganizationReference` for the id.
   *
   * The matching AC8 "clinic" filter dimension lives in
   * {@link auditAccessEventFiltersSchema} as `actorOrganizationReference`.
   */
  actorOrganization: z.string().optional(),
  /** `Organization/<id>` — the stable identifier, since display names change. */
  actorOrganizationReference: z.string().optional(),
  /**
   * Medplum `Login` id, from the access token's `login_id` claim (S17 — the ticket's
   * "Session ID", AC2's "session"). Stable for one sign-in and across token refreshes;
   * derived server-side from the Authorization header and never accepted from a client.
   * Optional because unauthenticated and machine-driven writes have no login.
   */
  sessionId: z.string().optional(),
  /** Server-assigned ISO timestamp. */
  recordedAt: z.string(),
  /** Human-readable activity, e.g. "Reviewed patient chart". */
  activity: z.string(),
  /** Chart section dataKeys touched during the access. */
  sections: z.array(z.string()),
  source: auditAccessSourceSchema,
  device: z.string().optional(),
  ipAddress: z.string().optional(),
  accessType: auditAccessTypeSchema,
  result: auditAccessResultSchema,
  /** True when the access was a download or export. */
  download: z.boolean(),
})
export type AuditAccessEvent = z.infer<typeof auditAccessEventSchema>

/**
 * Change actions. A tuple because since migration 0014 the values also back the
 * `audit_change_event.action` text+CHECK column.
 */
export const AUDIT_CHANGE_ACTION_VALUES = [
  /**
   * QA round 2 — the record was ADDED. Derived from a resource's FIRST version, which
   * the pair-diff loop structurally cannot see: it reads consecutive versions, and a
   * just-created resource has no predecessor to diff against. Every "add" in the chart
   * was therefore invisible (a new document, a placed order, a new insurance, the first
   * save of allergies/vitals/medications), even though the ticket opens its change list
   * with "For every create, update, delete, restore, sign, lock, amend or correction
   * event" and AC5 covers it.
   *
   * One event per created resource, NOT one per populated field: a create is a single
   * act, and a per-field fan-out would bury it under the fields it happened to set.
   * `field` is 'resource' and `after` summarizes it, mirroring how 'deleted' records the
   * opposite act.
   */
  'created',
  'updated',
  'deleted',
  /**
   * QA round 3 (re-test) — the record was REMOVED FROM THE CHART: a soft delete, the only
   * removal a clinician can actually perform (Documents → ⋮ → "Remove from chart", which
   * writes `status: entered-in-error` through `chartRecordRemove`).
   *
   * Its own value rather than a rendering of 'updated', for the same reason the note
   * lifecycle got 'signed'/'amended': the derivation records the ACT, and every consumer
   * downstream — the row, the detail card, both exports, and any future action filter —
   * reads the act from this field. Rendered as a removal while stored as 'updated', a
   * compliance reader scanning the trail for removals would find them on screen and miss
   * them in the CSV they actually file.
   *
   * NOT 'deleted', which stays reserved for a hard FHIR delete (a tombstone in the version
   * history, the record gone). These are different facts about the record's fate — one is
   * reversible by a super admin and carries a recorded reason, the other is not — and an
   * audit trail that spells them the same way cannot answer which happened.
   */
  'removed',
  'signed',
  'addendum',
  'amended',
] as const

/** One per-field change derived from consecutive FHIR resource versions. */
export const auditChangeEventSchema = z.object({
  patientId: z.string(),
  resourceType: z.string(),
  resourceId: z.string(),
  versionId: z.string().optional(),
  /**
   * 'updated' = per-field diff between consecutive versions;
   * 'deleted' = a delete tombstone in the version history (before carries the
   * last-known values, after is empty).
   *
   * Note-lifecycle actions (S15, DocumentReference only):
   * 'signed' = the docStatus preliminary→final transition (the lock);
   * 'addendum' = a NEW DocumentReference carrying relatesTo 'appends'
   * targeting the signed note (after carries the target reference);
   * 'amended' = any change to an already-final note (arriving via any path).
   */
  action: z.enum(AUDIT_CHANGE_ACTION_VALUES),
  /** `meta.lastUpdated` of the version that introduced the change. */
  recordedAt: z.string(),
  /** Top-level FHIR field that changed ('resource' for deletions). */
  field: z.string(),
  before: z.string().optional(),
  after: z.string().optional(),
  source: auditChangeSourceSchema,
  actorName: z.string().optional(),
  actorRole: z.string().optional(),
  /**
   * QA round 3 — the actor's stable FHIR reference (`Practitioner/<id>`,
   * `ClientApplication/<id>`), the ticket's "immutable user ID" for change events.
   * Display names change and collide; this does not. Also what lets the derivation
   * resolve the actor's app-level role after the fact — a display name cannot be
   * looked up, a reference can. Absent when the Provenance/meta.author carried no
   * reference at all.
   */
  actorReference: z.string().optional(),
  /**
   * QA round 2 — what the changed record IS, when its resource type does not say.
   *
   * A DocumentReference is a progress note, a consent PDF, a scribe transcript or the ID
   * card uploaded on the Demographics tab, and the row called all of them "Progress Note",
   * so replacing an ID produced "Updated Progress Note Status". Derived from the document's
   * own type as it stood at the moment of the change, because a compliance reader needs
   * what the record WAS then, not what it is now.
   *
   * Absent for every other tracked type, whose resource type already describes them, and
   * absent on rows derived before this field existed — the UI falls back to the resource
   * type's label, which is exactly what those rows always rendered.
   */
  resourceLabel: z.string().optional(),
  /**
   * The STABLE code behind {@link resourceLabel} — the document-type coding the document
   * carried at the moment of the change (`ProgressNote`, `ProgressNoteAddendum`,
   * `AuthorizationRelease`, `InsuranceCardFront`, `ScribeTranscript`, …), read from any
   * system that speaks that vocabulary: BeHuman's own, and Health Gorilla's, whose imports
   * carry the same codes. Absent for every non-DocumentReference type, whose resource type
   * already says what the record is.
   *
   * A second field rather than a reinterpretation of `resourceLabel`, because the two answer
   * different questions and only one of them is safe to filter on. `resourceLabel` is display
   * text ("Progress Note (Follow-up)"), authored per document and free to change; this is the
   * code the chart itself filters its sections by, so the audit's section filter and the
   * chart's sections agree on what a section contains.
   *
   * Absent also on rows derived before this field existed. The section predicates treat a
   * missing subtype as "could be any of the document sections" — those rows keep appearing
   * exactly where they always did, because over-inclusion is something a reader can see
   * through and omission is not.
   */
  /**
   * QA round 3 (re-test) — WHICH document the change was about, where `resourceLabel`
   * says what KIND it was. Every card read "Added id card" and every consent "Updated
   * consent", so a chart holding several documents of one type produced rows a reader
   * cannot tell apart. Taken from `DocumentReference.description` (falling back to the
   * attachment title), which is what the Documents viewer titles the record with, so the
   * audit and the chart call a document the same thing.
   *
   * Absent for every other resource type — their type IS their identity here — and on
   * rows derived before the field existed, which keep rendering by type exactly as before.
   */
  resourceName: z.string().optional(),
  resourceSubtype: z.string().optional(),

  // ---------------------------------------------------------------------------
  // S20 — the ticket's per-change metadata. All optional: these are DERIVED from
  // what the FHIR record happens to carry, and most historical versions carry
  // none of it. An absent field means "the record does not say", never "no".
  // ---------------------------------------------------------------------------
  /** "Reason for change, when required" — from `Provenance.reason`. */
  reason: z.string().optional(),
  /**
   * "Related appointment or encounter" — the changed resource's own encounter/context
   * reference. NOT from Provenance: FHIR R4 Provenance has no `encounter` element (its
   * elements are target, occurred*, recorded, policy, location, reason, activity, agent,
   * entity, signature — see @medplum/fhirtypes Provenance.d.ts:119-175).
   */
  relatedEncounter: z.string().optional(),
  /** "Reviewer or approver, when applicable" — a Provenance agent typed attester/verifier/legal-authenticator. */
  reviewer: z.string().optional(),
  /**
   * "Application version" and "correlation/request ID".
   *
   * DERIVATION LIMIT, not an oversight. Both are write-time request context; a FHIR
   * version records the resource, not the deploy or the request that produced it, so
   * neither can be recovered for a change that already happened. They are populated only
   * where a Provenance carries the extensions in AUDIT_PROVENANCE_EXTENSIONS
   * (change-history.service.ts) — nothing writes those today, verified by grep. Populating
   * them going forward means stamping every Provenance write path, which is a separate
   * ticket; see the PD-1322 follow-ups.
   */
  applicationVersion: z.string().optional(),
  correlationId: z.string().optional(),
})
export type AuditChangeEvent = z.infer<typeof auditChangeEventSchema>

/**
 * Filters applied IN THE QUERY, not after it. The store returns at most
 * {@link AUDIT_ACCESS_EVENT_PAGE_SIZE} rows, so filtering client-side would silently
 * answer "show me last March" from the newest page only — and the CSV export, which
 * exports whatever the table is showing, would inherit that hole. Every dimension a
 * SQL predicate can express lives here; free-text search stays client-side because it
 * only refines what is already on screen.
 */
export const auditAccessEventFiltersSchema = z.object({
  /** Inclusive lower bound on recordedAt (ISO 8601). */
  from: z.iso.datetime().optional(),
  /** Exclusive upper bound on recordedAt (ISO 8601). */
  to: z.iso.datetime().optional(),
  actorName: z.string().max(200).optional(),
  actorRole: z.string().max(200).optional(),
  /**
   * AC8's "clinic" dimension (S22). Matches on `actor_organization_reference` — the
   * `Organization/<id>` — NOT on the `actor_organization` display name.
   *
   * The reference is the stable identifier and the display name is not: an organization
   * that renames leaves rows carrying both spellings, so a name predicate would silently
   * return only the half written under whichever name the reader picked, in an
   * append-only table where the old rows can never be rewritten to agree.
   *
   * The UI still presents NAMES — this is not a reference the reader ever types. The
   * drawer's clinic select labels each option with `actorOrganization` and sends
   * `actorOrganizationReference` as its value (AuditFiltersDrawer.tsx), so the
   * name/reference split stays inside the option list. That is safe because the two are
   * written together: `resolveActorOrganization` (audit-actor-context.ts) sets the
   * reference whenever it sets a name, and falls back to the reference AS the name when
   * the Organization resource itself is not readable — so no row carries a clinic name
   * without a reference to match on.
   */
  actorOrganizationReference: z.string().max(200).optional(),
  source: auditAccessSourceSchema.optional(),
  result: auditAccessResultSchema.optional(),
  accessType: auditAccessTypeSchema.optional(),
  /** True = only download/export events (the drawer's downloads-and-exports toggle). */
  downloadsOnly: queryBooleanSchema.optional(),
  /** Chart section dataKey that must appear in the event's sections array. */
  section: z.string().max(200).optional(),
})
export type AuditAccessEventFilters = z.infer<typeof auditAccessEventFiltersSchema>

/**
 * The paging half of a list response, shared by both tabs.
 *
 * Split out of the old single `truncated` flag, which collapsed two unrelated facts into
 * one boolean: "this page is full" and, on the changes tab only, "the materialization has
 * not converged yet". They need different answers — a full page is fixed by narrowing the
 * window, an unconverged sync is fixed by waiting — and a client that cannot tell them
 * apart either polls forever or stops polling too early. `capped` is the paging fact and
 * lives here; `syncPending` is a change-events-only fact and lives on that output alone.
 */
export const auditPageWindowSchema = z.object({
  /**
   * True when the page cap was hit, so older matching events exist that this
   * response does not contain. The UI must say so rather than let a compliance
   * reader believe they are looking at the whole record.
   */
  capped: z.boolean(),
  /**
   * Where the NEXT window starts: send it back as the `to` filter to read the events this
   * page could not carry. Exclusive, and the page is trimmed to match, so consecutive
   * windows are gapless and non-overlapping — no event is read twice and none is skipped.
   *
   * Present only when the page is `capped` AND the cut lands on a timestamp boundary. It
   * is ABSENT when a capped page turns out to be a single `recordedAt` all the way
   * through: no `to` bound can move past that instant without stepping over rows the page
   * never carried, so the honest answer is "narrow the other filters" rather than a cursor
   * that silently loses events.
   */
  nextWindowTo: z.string().optional(),
})
export type AuditPageWindow = z.infer<typeof auditPageWindowSchema>

export const auditListAccessEvents = oc
  .route({ method: 'GET', path: '/v2/patients/{patientId}/audit/access-events' })
  .input(z.object({ patientId: z.string() }).extend(auditAccessEventFiltersSchema.shape))
  .output(z.object({ events: z.array(auditAccessEventSchema) }).extend(auditPageWindowSchema.shape))

/**
 * The values the Audit tab's filters can offer for one patient, across the WHOLE record.
 *
 * Greptile round 2 on !1354. The filter option lists were built from the events a response
 * happened to return, and every response is bounded — by the active date range (30 days by
 * default) and by the page cap. So an actor who last touched the chart in March was not
 * offered in the user filter, and could only be found by first widening the date range to
 * discover that they exist. A filter whose options depend on the filter's own result is
 * circular: it can only ever offer what you have already narrowed to.
 *
 * This is deliberately a SEPARATE call rather than extra fields on the list responses. The
 * list endpoints answer "what matches these filters"; this answers "what could be filtered
 * to at all", which is a different question with a different lifetime — it changes only when
 * the patient's record does, so the client fetches it once per patient instead of on every
 * filter change.
 *
 * Sourced from BOTH audit tables, because the selects are shared by both tabs.
 */
export const auditFilterOptionsSchema = z.object({
  /** Distinct actor names, from access events and change events alike. */
  users: z.array(z.string()),
  /** Distinct chart-section keys seen on access events, plus those the change-tracked resource types map to. */
  sections: z.array(z.string()),
  /** Distinct clinics as reference -> display name. Access events only; a change event records no organization. */
  clinics: z.array(z.object({ reference: z.string(), name: z.string() })),
  /**
   * QA round 3 (independent re-test) — distinct actor roles, from access events and change
   * events alike, for exactly the reason `users` and `sections` are derived rather than
   * declared.
   *
   * The role select was a HARDCODED list of the six app-level roles (Physician, Care
   * Coordinator, Call Center Manager, Healthcare Service Manager, Super Admin, System
   * Service). Real rows do not carry only those. On dev, one patient's access trail held
   * `Practitioner` 855 times and `Patient` 15 — 66% of the rows unreachable by ANY role the
   * dropdown offered — and change rows additionally carry `Recorder`, `Verifier` and
   * `Performer`, which are Provenance agent types rather than app roles at all.
   *
   * A hardcoded list can never catch up, either: both tables are append-only and a row
   * keeps whatever role string it was written with, so every role vocabulary that has ever
   * been in production stays reachable in the data forever. Only the data can say what is
   * in it.
   */
  roles: z.array(z.string()),
})
export type AuditFilterOptions = z.infer<typeof auditFilterOptionsSchema>

export const auditListFilterOptions = oc
  .route({ method: 'GET', path: '/v2/patients/{patientId}/audit/filter-options' })
  .input(z.object({ patientId: z.string() }))
  .output(auditFilterOptionsSchema)

/** Rows one change-events response may carry before it reports itself truncated. */
export const AUDIT_CHANGE_EVENT_PAGE_SIZE = 1_000

/**
 * Change-history filters, applied IN THE QUERY over the materialized
 * `audit_change_event` table — same rule as {@link auditAccessEventFiltersSchema}, and for
 * the same reason: filtering after a bound answers a date-range question from whatever
 * happened to fit under the bound. This is the Greptile P1 fix on !1336 (2026-08-06):
 * date/actor/role/source used to run in the browser AFTER the server's per-type resource
 * caps, so a chart that hit a cap could silently omit matching events. Free-text search
 * stays client-side because it only refines what is already on screen.
 */
export const auditChangeEventFiltersSchema = z.object({
  /**
   * Chart section dataKey; omitted = all change-tracked sections. Maps server-side to the
   * section's resource types (SECTION_RESOURCE_TYPES). The ONLY filter that narrows the
   * derivation walk — a date/actor filter must never shrink what gets derived, only what
   * gets returned.
   */
  section: z.string().max(200).optional(),
  /** Inclusive lower bound on recordedAt (ISO 8601). */
  from: z.iso.datetime().optional(),
  /** Exclusive upper bound on recordedAt (ISO 8601). */
  to: z.iso.datetime().optional(),
  actorName: z.string().max(200).optional(),
  actorRole: z.string().max(200).optional(),
  source: auditChangeSourceSchema.optional(),
})
export type AuditChangeEventFilters = z.infer<typeof auditChangeEventFiltersSchema>

// Sync-then-query: the handler first materializes any FHIR versions the
// `audit_change_event` table has not seen (derivation unchanged), then answers from the
// table with the filters as SQL predicates. `section` narrows the sync fan-out to that
// section's resource types (lazy per-section loading); every other filter applies only
// to the query.
export const auditListChangeEvents = oc
  .route({ method: 'GET', path: '/v2/patients/{patientId}/audit/change-events' })
  .input(z.object({ patientId: z.string() }).extend(auditChangeEventFiltersSchema.shape))
  .output(
    z
      .object({
        events: z.array(auditChangeEventSchema),
        /**
         * True while the MATERIALIZATION is not yet known complete: a walked type whose
         * backfill has not finished (the per-request sync budget ran out — the persisted
         * cursor in `audit_change_sync_state` means the NEXT request continues where this
         * one stopped), or a resource with an outstanding per-resource history cursor (its
         * version history outgrew one request's page budget; `audit_change_history_cursor`
         * resumes the walk next request). It CONVERGES: search pages, resources, and a
         * single resource's versions each resume from a persisted cursor, so the first view
         * of a very large chart honestly reports a partial record and a few requests later
         * the walks complete and the flag clears.
         *
         * Deliberately NOT the row cap, which is {@link auditPageWindowSchema}'s `capped`.
         * The two used to be OR-ed into one `truncated` boolean, and a client polling until
         * it cleared could never stop on a chart big enough to fill the page every time —
         * the cap does not converge, so the OR never did either. Refetch on `syncPending`;
         * offer a narrower window on `capped`.
         *
         * Every consumer (tab banner, CSV, PDF) must still say the record is incomplete
         * while either flag is set, rather than presenting the list as whole.
         */
        syncPending: z.boolean(),
      })
      .extend(auditPageWindowSchema.shape),
  )

/**
 * The web UI reports that chart sections were viewed. The server authors the
 * actual audit record (actor, timestamp, source, result) — the client cannot
 * write an audit record itself. Active-time/heartbeat tracking (Jira PD-1322
 * comment 24728) was resolved by removal: duration tracking is out of the MVP
 * (Alex, 2026-07-30), so this endpoint reports only WHAT was viewed.
 */
export const auditReportViewInputSchema = z.object({
  patientId: z.string(),
  // What was viewed stays in scope; duration tracking was cut from the
  // MVP (Alex, 2026-07-30) — no time fields are accepted.
  //
  // A CLOSED VOCABULARY ({@link AUDIT_SECTION_KEYS}), not merely a bounded string. The
  // size caps below came first and are still needed — this payload becomes a jsonb column
  // in an append-only table that nothing can delete, the route bypasses the interceptor's
  // dedupe window (the handler authors its own record), and apiBodySize allows 50MB — but
  // they bound the VOLUME an abusive caller can write, not the CONTENT. The content is
  // what reaches other readers: `/audit/filter-options` derives the Section dropdown from
  // the sections rows were written with, so an arbitrary string was an authenticated
  // caller writing permanent text into any patient's compliance filter list.
  //
  // `.max()` is retained even though the enum bounds the value set at 23: it is the cap on
  // how many entries one row may carry, and a caller may repeat a key.
  sections: z.array(auditSectionKeySchema).min(1).max(AUDIT_SECTIONS_MAX_COUNT),
})

export const auditReportView = oc
  .route({ method: 'POST', path: '/v2/patients/{patientId}/audit/view-events' })
  .input(auditReportViewInputSchema)
  .output(z.object({ id: z.string(), recordedAt: z.string() }))

/**
 * Coarse client-reportable audit actions (S13/S14). The client sends ONLY the
 * action code — activity text, sections, and the download flag are fixed
 * server-side per action (see ACTION_EVENT_FIELDS in audit.service.ts), and
 * actor/timestamp are authored by the server like every other audit record.
 */
export const auditActionSchema = z.enum([
  /** The CSV artefact. Keeps its original code — persisted rows carry the activity text. */
  'audit-export',
  /**
   * The PDF artefact (S11). A separate code rather than a format field on `audit-export`
   * because ACTION_EVENT_FIELDS is what fixes the activity text server-side, and a
   * compliance reader looking at an Access History row has to be able to tell which
   * artefact left the building.
   */
  'audit-export-pdf',
  'document-open',
  'document-download',
  /**
   * QA round 1 (M3) — what the "Export to PDF" control produces. A separate code from
   * `document-download` for the same reason `audit-export-pdf` is separate from
   * `audit-export`: the activity text is fixed server-side per code, and a compliance
   * reader has to be able to tell an export artefact from a plain download.
   */
  'document-export',
  /**
   * S18 — the browser's `beforeprint` signal. Captures print INTENT (the print dialog
   * opening), not confirmed paper output: no browser exposes whether the user completed
   * or cancelled the dialog, or whether a printer produced a page.
   */
  'chart-print',
  /**
   * QA round 3 — opening a chart RECORD that is not (or not only) a document: an order
   * (with or without attached paperwork), a lab/imaging report. Round 1's instrumentation
   * keyed open events on renderable DocumentReference attachments, so an order without a
   * PDF logged nothing at all, and a lab open carried neither the lab's name nor its
   * section. The server names and sections the event from the record itself — see
   * `record` below.
   */
  'record-open',
])
export type AuditAction = z.infer<typeof auditActionSchema>

/** The record types the web reports opens for; the server reads and verifies each. */
export const AUDIT_RECORD_TYPES = ['DocumentReference', 'RequestGroup', 'DiagnosticReport'] as const
export const auditRecordReferenceSchema = z.object({
  resourceType: z.enum(AUDIT_RECORD_TYPES),
  id: z.string().max(200),
})
export type AuditRecordReference = z.infer<typeof auditRecordReferenceSchema>

/**
 * Sibling of {@link auditReportView} for non-view actions: exporting the audit
 * CSV (S13) and opening/downloading patient documents (S14). Kept separate so
 * the view-events contract stays sections-only.
 */
export const auditReportActionInputSchema = z.object({
  patientId: z.string(),
  action: auditActionSchema,
  /**
   * QA round 1 (M2) — the document a document-shaped action was about. NOT trusted
   * for anything it says: the SERVER reads the DocumentReference through the
   * caller's own client and derives the section and the activity title from the
   * resource itself, so a client can only ever name a document its access policy
   * lets it read (and only one belonging to this patient — the server checks the
   * subject). Unreadable, absent, or wrong-patient ids fall back to the generic
   * per-action record. Ignored for non-document actions.
   */
  documentReferenceId: z.string().max(200).optional(),
  /**
   * QA round 3 — the generalization of `documentReferenceId` to any openable chart
   * record (order groups and lab reports open without any DocumentReference existing).
   * Same no-trust contract: the server reads the named resource through the CALLER's
   * client, requires its subject to be this patient, and derives the section and title
   * from the resource itself. `documentReferenceId` remains accepted as the
   * DocumentReference shorthand.
   */
  record: auditRecordReferenceSchema.optional(),
})

export const auditReportAction = oc
  .route({ method: 'POST', path: '/v2/patients/{patientId}/audit/action-events' })
  .input(auditReportActionInputSchema)
  .output(z.object({ id: z.string(), recordedAt: z.string() }))
